import {
  selectAiReviewAnnotations,
  selectCurrentAnnotation,
} from "@/store/ai-asset-review/ai-asset-review-selectors";
import { AiGeneratedAnnotation } from "@/store/ai-asset-review/ai-asset-review-slice";
import { useAppSelector } from "@/store/store-hooks";
import { assert } from "@faro-lotv/foundation";
import { Box } from "@mui/material";
import { useEffect, type JSX } from "react";
import { List, useListCallbackRef, type RowComponentProps } from "react-window";
import { AiAnnotationListItem } from "./ai-review-components/ai-annotation-list-item";

const AI_ANNOTATION_ITEM_HEIGHT = 212;

/** @returns list view component for reviewing all annotations at once */
export function AiReviewListView(): JSX.Element {
  const annotations = useAppSelector(selectAiReviewAnnotations);
  const currentAnnotation = useAppSelector(selectCurrentAnnotation);
  const [list, setList] = useListCallbackRef(null);

  useEffect(() => {
    if (currentAnnotation && list) {
      const index = annotations.findIndex(
        (annotation) => annotation.id === currentAnnotation.id,
      );

      if (index !== -1) {
        list.scrollToRow({
          index,
          align: "start",
        });
      }
    }
  }, [currentAnnotation, currentAnnotation?.id, annotations, list]);

  return (
    <Box component="div" sx={{ flexGrow: 1, height: "100%", m: 0, p: 0 }}>
      <List<AiAnnotationItemProps>
        listRef={setList}
        rowComponent={AiAnnotationItem}
        overscanCount={5}
        rowCount={annotations.length}
        rowHeight={AI_ANNOTATION_ITEM_HEIGHT}
        rowProps={{ annotations }}
      />
    </Box>
  );
}

/** Properties for a single AI annotation list item */
type AiAnnotationItemProps = {
  /** List data */
  annotations: AiGeneratedAnnotation[];
};

/** @returns A single AI annotation item in the list, managed by react-window */
function AiAnnotationItem({
  index,
  annotations,
  style,
}: RowComponentProps<AiAnnotationItemProps>): JSX.Element {
  const annotation = annotations[index];
  assert(annotation, "Invalid annotation data");

  return <AiAnnotationListItem annotation={annotation} sx={{ ...style }} />;
}
